/*
 * Copyright © 2023 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

import React, { ReactElement, useContext } from "react"
import Video from "@medaica/common/components/video"
import { observer } from "mobx-react-lite"
import VirtualExamContext from "views/exam/virtual-exam/virtual-exam-context"
import VideoContainer from "@medaica/common/views/exam/virtual-exam/exam-room/components/video-container"
import AudioMutedIcon from "@medaica/common/components/AudioMutedIcon"
import Target from "./target"
import { Box, Tooltip } from "@mui/material"
import { deviceIsM1 } from "@medaica/common/services/m1"
import RemoteParticipantStore from "@medaica/common/views/exam/virtual-exam/stores/av-store/remote-participant-store"
import PatientVirtualExamMediaDeviceStore from "views/exam/virtual-exam/views/exam-room/stores/patient-virtual-exam-media-device-store"
import ParticipantNameLabel from "@medaica/common/views/exam/virtual-exam/exam-room/components/participant-name-label"
import BadNoiseIndicator from "@medaica/common/views/exam/virtual-exam/components/bad-noise-indicator"
import RecordingStatusOverlay, { MutedMessage } from "./recording-status-overlay"
import { fitVideo, Size, useVideoAspectRatio } from "@medaica/common/views/exam/virtual-exam/video-layout"
import { providerVideoCaptureSize } from "@medaica/common/const"

// The healthcare provider's picture sits over the top right corner of the patient's, at this share of its height:
// 200 px on the 625 px tall picture this page had before the picture took the size of the screen
const remoteVideoHeightShare = 200 / 625

const StethoscopeInfo = observer(
  ({ mediaDeviceStore }: { mediaDeviceStore: PatientVirtualExamMediaDeviceStore }): ReactElement => {
    return (
      <div
        className="absolute bottom-2.5 right-2.5 text-white text-sm z-15 bg-black rounded-lg py-1 px-2.5
  bg-opacity-40 flex items-center"
      >
        {mediaDeviceStore.isM1Connected ? (
          <Tooltip title={mediaDeviceStore.m1Info?.label}>
            <div className="flex items-center">
              <div className="h-3 w-3 rounded-full bg-green-400 mr-2" />
              Stethoscope connected
            </div>
          </Tooltip>
        ) : (
          <>
            <div className="h-3 w-3 rounded-full bg-red-400 mr-2" />
            Stethoscope disconnected
          </>
        )}
      </div>
    )
  }
)

const MutedIndicator = () => {
  return (
    <Box
      sx={(theme) => ({
        position: "absolute",
        top: theme.spacing(0.5),
        left: theme.spacing(0.5),
        zIndex: 10,
      })}
    >
      <AudioMutedIcon />
    </Box>
  )
}

const RemoteParticipantVideo = observer(
  ({ remoteParticipant, height }: { remoteParticipant: RemoteParticipantStore; height: number }): ReactElement => {
    const { virtualExamStore } = useContext(VirtualExamContext)
    const providerAspectRatio = providerVideoCaptureSize.width / providerVideoCaptureSize.height
    const aspectRatio = useVideoAspectRatio(remoteParticipant.videoTrack, providerAspectRatio)
    return (
      <Box
        sx={(theme) => ({
          position: "absolute",
          top: theme.spacing(0.5),
          right: theme.spacing(0.5),
          zIndex: 10,
          width: `${Math.floor(height * aspectRatio)}px`,
          height: `${height}px`,
          backgroundColor: "#F0F0FB",
        })}
        className="panel"
      >
        <Video
          videoTrack={remoteParticipant.videoTrack}
          audioTrack={remoteParticipant.audioTrack}
          isLocal={false}
          isMuted={remoteParticipant.videoIsMuted}
          overlay={
            <>
              {((remoteParticipant.auscultationAudioTrack && remoteParticipant.auscultationAudioIsMuted) ||
                (!remoteParticipant.auscultationAudioTrack && remoteParticipant.audioIsMuted)) && <MutedIndicator />}
              <ParticipantNameLabel name={virtualExamStore.healthcareProvider.displayName} />
            </>
          }
        />
      </Box>
    )
  }
)

/**
 * The patient's picture, as large as fits in `area`, with the healthcare provider's picture over its top right corner.
 */
const VideoSet = observer(({ area }: { area: Size }): ReactElement => {
  const { avStore, virtualExamStore, mediaDeviceStore } = useContext(VirtualExamContext)
  const captureSize = mediaDeviceStore.videoCaptureSize
  const localAspectRatio = useVideoAspectRatio(
    mediaDeviceStore.videoDevice?.track,
    captureSize.width / captureSize.height
  )
  const local = fitVideo(area, localAspectRatio)

  return (
    <VideoContainer size={local}>
      {virtualExamStore.isReviewingAuscultation ? <MutedMessage /> : <RecordingStatusOverlay />}
      {avStore.remoteParticipant && (
        <RemoteParticipantVideo
          remoteParticipant={avStore.remoteParticipant}
          height={Math.floor(local.height * remoteVideoHeightShare)}
        />
      )}
      <Video
        videoTrack={mediaDeviceStore.videoDevice?.track}
        audioTrack={mediaDeviceStore.audioDevice?.track}
        isLocal={true}
        isMuted={mediaDeviceStore.videoDeviceIsMuted}
      />
      {virtualExamStore.badNoiseDetector && deviceIsM1(mediaDeviceStore.audioDevice) && (
        <BadNoiseIndicator
          isVisible={virtualExamStore.badNoiseDetector.isBadNoiseDetected}
          isMicrophoneMuted={mediaDeviceStore.audioDeviceIsMuted}
        />
      )}
      <StethoscopeInfo mediaDeviceStore={mediaDeviceStore} />
      {virtualExamStore.isTargetEnabled && <Target />}
    </VideoContainer>
  )
})

export default VideoSet
